/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Fedorov Maxim
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.15.0
 * @since 1.0.1
 */
import { Fragment, memo, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Skeleton } from '../../../../../../components/common';
import { LayoutStyled } from '../../../../../../components/common/Block/Layout';
import { pushGTMAnalytic } from '../../../../../../modules/analytics/GoogleTagManager/GTMActions';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import initTranslationCommon from '../../../../../../modules/internationalization/constants/common';
import initTranslationPageDashboard
    from '../../../../../../modules/internationalization/constants/pages/dashboard/dashboard';
import { IPageDashboardTranslation } from '../../../../../../modules/internationalization/types';
import ICommonTranslation from '../../../../../../modules/internationalization/types/common';
import { guardComponentType } from '../../../../../../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../../../../../modules/role/selectors/guardSelector';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { WidgetSkeletonParagraph } from '../../constants/widgetSkeletonContants';
import { WidgetTitleStyled } from '../../styled/WidgetStyled';
import WidgetError from '../../WidgetError';
import { IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { EWidgetStatus } from '../../widgetTypes';
import { WidgetPaymentHistoryStyled } from '../styled/widgetPaymentHistoryStyled';
import {
    makeSelectorPaymentHistoryGetPayments,
    makeSelectorPaymentHistoryIsError,
    makeSelectorPaymentHistoryIsLoading,
    makeSelectorPaymentHistoryStatus,
} from '../widgetPayentHistorySelects';
import { cleanListPaymentHistory, widgetPaymentHistoryRequest } from '../widgetPaymentHistoryActions';
import { IPaymentsHistoryItem } from '../widgetPaymentHistoryTypes';
import WidgetPaymentHistoryEmpty from './WidgetPaymentHistoryEmpty';
import WidgetPaymentHistorySuccessful from './WidgetPaymentHistorySuccessful';

interface IConnectedProps {
    componentsFeature: guardComponentType;
    error: string;
    loading: boolean;
    payments: IPaymentsHistoryItem[];
    selectedAccount: IWidgetSearchAccountsItem;
    status: EWidgetStatus;
}

interface IWidgetPaymentHistoryDispatchToProps {
    cleanListSubscribers: () => void;
    loadPayments: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

interface IWidgetPaymentHistoryProps {
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetPaymentHistory = IWidgetPaymentHistoryProps & IConnectedProps & IWidgetPaymentHistoryDispatchToProps;

export const WidgetPaymentHistory = (props: TWidgetPaymentHistory) => {
    const {
        cleanListSubscribers,
        componentsFeature,
        error,
        loading,
        loadPayments,
        payments,
        status,
        translation,
    } = props;

    useEffect(() => {
        cleanListSubscribers();
        loadPayments();
    }, [props.selectedAccount.id]);

    return (
        <WidgetPaymentHistoryStyled
            data-widget='WidgetPaymentHistory'
            error={error}
            isEmpty={payments.length <= 0}
        >
            <LayoutStyled className="mb-4">
                <WidgetTitleStyled>
                    {translation.widgets.paymentHistory.title}
                </WidgetTitleStyled>
            </LayoutStyled>
            <Skeleton
                count={3}
                isLoading={loading}
                render={() => WidgetSkeletonParagraph}
            >
                <Fragment>
                    {{
                        empty: <WidgetPaymentHistoryEmpty
                            translation={translation}/>,
                        error: <WidgetError
                            text={translation.widgets.paymentHistory.fail.text}/>,
                        success: <WidgetPaymentHistorySuccessful
                            accountId={props.selectedAccount.id}
                            componentsFeature={componentsFeature}
                            payments={payments}
                            pushGtmAnalytic={props.pushGtmAnalytic}
                            translation={translation}
                        />,
                    }[status]}
                </Fragment>
            </Skeleton>
        </WidgetPaymentHistoryStyled>
    );
};

WidgetPaymentHistory.defaultProps = {
    translation: { ...initTranslationPageDashboard, ...initTranslationCommon },
};

const stateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    error: makeSelectorPaymentHistoryIsError(),
    loading: makeSelectorPaymentHistoryIsLoading(),
    payments: makeSelectorPaymentHistoryGetPayments(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
    status: makeSelectorPaymentHistoryStatus(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IWidgetPaymentHistoryDispatchToProps => ({
    cleanListSubscribers: () => dispatch(cleanListPaymentHistory()),
    loadPayments: () => dispatch(widgetPaymentHistoryRequest()),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(stateToProps, mapDispatchToProps)(memo(WidgetPaymentHistory));
